import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import {
    IAuthCredentials,
    IAuthRecovery,
    IAuthReset,
    ILoginResponse,
    IUserAuthenticated,
} from '@models/auth/auth.models';
import { IGeneralResponse } from '@models/response/response.model';
import { Observable, catchError, map, of, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private apiUrl: string = environment.apiEndpoint;
    private defaultUrl: string = environment.default;
    private http: HttpClient = inject(HttpClient);
    private router: Router = inject(Router);

    loggedIn: WritableSignal<boolean> = signal(false);
    authenticatedUser: WritableSignal<IUserAuthenticated | null> = signal(null);

    login(credentials: IAuthCredentials): Observable<Record<string, string>> {
        return this.http.post<ILoginResponse>(`${this.apiUrl}/auth/login`, credentials).pipe(
            map((response: ILoginResponse) => {
                sessionStorage.setItem('econsentAccessToken', response.access_token);

                this.authenticatedUser.set(response.user);
                this.loggedIn.set(true);
                this.storeUser({
                    name: response.user.name,
                    email: response.user.email,
                    public_id: response.user.public_id,
                    doctor_id: response.user.doctor_id,
                    role: response.user.role,
                    individual_plan: response.user.individual_plan,
                    plan_name: response.user.plan_name,
                    plan_value: response.user.plan_value,
                    max_doctors: response.user.max_doctors,
                    plan_billing: response.user.plan_billing,
                    free_until: response.user.free_until,
                    trial_until: response.user.trial_until,
                    trial_active: response.user.trial_active,
                    valid_until: response.user.valid_until,
                    owner_id: response.user.owner_id,
                    compliance: response.user.compliance,
                    payment_method: response.user.payment_method,
                    billing_status: response.user.billing_status,
                    billing_status_label: response.user.billing_status_label,
                    can_create_terms: response.user.can_create_terms,
                    pending_boleto_payment: response.user.pending_boleto_payment,
                    pending_boleto_due_date: response.user.pending_boleto_due_date,
                    pending_boleto_pdf: response.user.pending_boleto_pdf,
                    current_boleto: response.user.current_boleto,
                    grace_until: response.user.grace_until,
                    blocking_reason: response.user.blocking_reason,
                });

                return {
                    title: response.title ?? '',
                    msg: response.msg,
                };
            }),
            catchError((err) => {
                this.storeUser(null);
                this.loggedIn.set(false);
                const payload = err?.error ?? err ?? {};
                const msg =
                    payload?.msg ?? err?.message ?? (typeof err === 'string' ? err : JSON.stringify(payload || err));
                return throwError(() => ({ msg }));
            }),
        );
    }

    recovery(data: IAuthRecovery): Observable<IGeneralResponse> {
        return this.http.post<IGeneralResponse>(`${this.apiUrl}/auth/send-recovery-token`, data).pipe(
            map((response: IGeneralResponse) => {
                return {
                    title: response.title ?? '',
                    msg: response.msg,
                    status: response.status,
                };
            }),
            catchError((err) => {
                const payload = err?.error ?? err ?? {};
                const msg =
                    payload?.msg ?? err?.message ?? (typeof err === 'string' ? err : JSON.stringify(payload || err));
                return throwError(() => ({ msg }));
            }),
        );
    }

    reset(data: IAuthReset): Observable<IGeneralResponse> {
        return this.http.post<IGeneralResponse>(`${this.apiUrl}/auth/reset-password`, data).pipe(
            map((response: IGeneralResponse) => {
                return {
                    title: response.title ?? '',
                    msg: response.msg,
                    status: response.status,
                };
            }),
            catchError((err) => {
                const payload = err?.error ?? err ?? {};
                const msg =
                    payload?.msg ?? err?.message ?? (typeof err === 'string' ? err : JSON.stringify(payload || err));
                return throwError(() => ({ msg }));
            }),
        );
    }

    logout(): void {
        this.storeUser(null);
        this.loggedIn.set(false);
        this.router.navigate(['/admin/auth/login']);
        sessionStorage.removeItem('econsentAccessToken');
    }

    getUser(): IUserAuthenticated | null {
        const userFromStorage: string | null = sessionStorage.getItem('econsentCurrentUser');

        if (!userFromStorage) return null;

        return JSON.parse(userFromStorage) as IUserAuthenticated;
    }

    updateUser(userPatch: Partial<IUserAuthenticated>): IUserAuthenticated | null {
        const currentUser = this.getUser();
        if (!currentUser) return null;

        const user = { ...currentUser, ...userPatch };
        this.authenticatedUser.set(user);
        this.storeUser(user);

        return user;
    }

    getCsrfCookie() {
        return this.http.get(`${this.defaultUrl}sanctum/csrf-cookie`).pipe(
            map((response) => {
                return response;
            }),
            catchError(() => {
                return [false];
            }),
        );
    }

    verifyToken(): Observable<boolean> {
        return this.http.get<any>(`${this.apiUrl}/auth/verify`).pipe(
            map(() => {
                return true;
            }),
            catchError(() => {
                return [false];
            }),
        );
    }

    getToken(): string | null {
        return sessionStorage.getItem('econsentAccessToken');
    }

    getUserRole(): Observable<string | null> {
        const headers = new HttpHeaders().set('X-Load-UserRole', 'true');
        return this.http
            .get<{
                role: string;
            }>(`${this.apiUrl}/user/retrieveRole`, { headers })
            .pipe(
                map((response) => response.role),
                catchError((err) => {
                    console.error(err);
                    return of(null);
                }),
            );
    }

    private storeUser(user: any) {
        if (user == null) {
            sessionStorage.removeItem('econsentCurrentUser');
            return false;
        }

        sessionStorage.setItem('econsentCurrentUser', JSON.stringify(user));
        this.loggedIn.set(true);
        return true;
    }
}
